import type { ReactElement } from 'react';
import { AdminStatTile, type AdminStatTileTone } from '@/components/atoms/admin-stat-tile';
import { AdminCardError } from '@/components/atoms/admin-card-error';
import { useOverviewStats, type OverviewStats } from '@/lib/queries/admin';

const SKELETON_LABELS = ['Pending', 'Flagged', 'Pieces', 'Users', 'Storage'] as const;

export function AdminOverviewStatsRow(): ReactElement {
  const { data, isError } = useOverviewStats();

  if (isError) {
    return <AdminCardError label="overview stats" />;
  }

  if (!data) {
    return (
      <div className="grid grid-cols-5 gap-3">
        {SKELETON_LABELS.map((label) => (
          <AdminStatTile key={label} label={label} value="…" tone="neutral" />
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-5 gap-3">
      <AdminStatTile {...pendingTile(data)} />
      <AdminStatTile {...flaggedTile(data)} />
      <AdminStatTile {...piecesTile(data)} />
      <AdminStatTile {...usersTile(data)} />
      <AdminStatTile {...storageTile(data)} />
    </div>
  );
}

type TileSpec = {
  label: string;
  value: string;
  delta?: string;
  subLabel?: string;
  tone: AdminStatTileTone;
};

function pendingTile(s: OverviewStats): TileSpec {
  return {
    label: 'Pending',
    value: String(s.pendingCount),
    delta: s.pendingDeltaSinceYesterday !== 0 ? deltaSinceYesterday(s.pendingDeltaSinceYesterday) : undefined,
    tone: s.pendingCount > 0 ? 'warn' : 'neutral',
  };
}

function flaggedTile(s: OverviewStats): TileSpec {
  return {
    label: 'Flagged',
    value: String(s.flaggedCount),
    delta: s.flaggedDeltaSinceYesterday !== 0 ? deltaSinceYesterday(s.flaggedDeltaSinceYesterday) : undefined,
    tone: s.flaggedCount > 0 ? 'warn' : 'neutral',
  };
}

function piecesTile(s: OverviewStats): TileSpec {
  return {
    label: 'Pieces',
    value: String(s.pieceCount),
    delta: s.piecesAddedThisWeek > 0 ? `+${s.piecesAddedThisWeek} this week` : undefined,
    tone: s.piecesAddedThisWeek > 0 ? 'ok' : 'neutral',
  };
}

function usersTile(s: OverviewStats): TileSpec {
  return {
    label: 'Users',
    value: String(s.userCount),
    delta: s.usersAddedThisWeek > 0 ? `+${s.usersAddedThisWeek} this week` : undefined,
    tone: s.usersAddedThisWeek > 0 ? 'ok' : 'neutral',
  };
}

function storageTile(s: OverviewStats): TileSpec {
  if (s.storageBytesCapacity === 0) {
    return { label: 'Storage', value: '—', subLabel: 'data coming soon', tone: 'neutral' };
  }
  const pct = Math.round((s.storageBytesUsed / s.storageBytesCapacity) * 100);
  return { label: 'Storage', value: `${pct}%`, tone: 'neutral' };
}

function deltaSinceYesterday(delta: number): string {
  const sign = delta > 0 ? '+' : '';
  return `${sign}${delta} since yesterday`;
}
